<template>
    <div>
        <div class="chart-of-accounts-create-update-list-new">
            <div class="chart-of-accounts-create-update-list-new-input">
                <InputText title="Plano de Conta" field="name" :formName="formName" :required="true" :maxLength="40"
                    v-model="chartOfAccounts.name" :markFormDirty="false" :enter="create" />
            </div>
            <div class="chart-of-accounts-create-update-list-new-button">
                <Button _key="btnSaveChartOfAccounts" type="primary" :disabled="!isFormValid(formName)"
                    classIcon="fa-solid fa-plus" size="small" :clicked="create" />
            </div>
        </div>
        <ScrollBar :minHeight="0" :maxHeight="260">
            <Loading type="line" :center="false" v-if="loading" />
            <ul class="chart-of-accounts-create-update-list-clean-list">
                <li v-for="item in items" :key="item.id" class="chart-of-accounts-create-update-list-item-row">
                    <div class="chart-of-accounts-create-update-list-item-icon"
                        :style="'background:' + costCenter.color">
                        <span :class="costCenter.icon"></span>
                    </div>
                    <div class="chart-of-accounts-create-update-list-item-name" :title="item.name">
                        <InputTextEdit v-model="item.name" :params="item" :enter="update" />
                    </div>
                    <div class="chart-of-accounts-create-update-list-item-actions">
                        <Button :_key="'btnRemove_' + item.id" type="danger" classIcon="fas fa-trash" size="small"
                            :params="item" :clicked="remove" />
                    </div>
                </li>

                <li v-if="!items || items.length === 0" class="chart-of-accounts-create-update-list-empty-state">
                    Nenhum plano de conta ainda.
                </li>
            </ul>
        </ScrollBar>
    </div>
</template>

<script>

import Button from "@/ui/forms/Button";
import InputText from "@/ui/forms/InputText";
import InputTextEdit from "@/ui/forms/InputTextEdit.vue";
import Loading from "@/ui/shared/Loading.vue";
import ScrollBar from "@/ui/layout/ScrollBar";

import ChartOfAccounts from "../../models/ChartOfAccounts";
import ChartOfAccountsService from "../../services/chartOfAccounts.service";

import { mapGetters, mapMutations } from "vuex";

export default {
    name: "ChartOfAccountsCreateUpdateList",
    props: {
        costCenter: Object,
    },
    components: { Button, InputText, InputTextEdit, Loading, ScrollBar },
    data() {
        return {
            formName: "chartOfAccountsCreateUpdateList",
            chartOfAccounts: new ChartOfAccounts(),
            items: [],
            loading: false,
        };
    },
    computed: {
        ...mapGetters("validation", ["isFormValid"]),
        ...mapGetters("generic", ["event"]),
    },
    mounted() {
        this.getAll();
    },
    methods: {
        ...mapMutations("generic", ["addEvent", "removeLoading", "hideModal", "hideVodal"]),
        ...mapMutations("validation", ["resetValidation"]),
        async getAll() {
            this.loading = true;
            const ccId = this.costCenter && this.costCenter.id ? this.costCenter.id : null;
            const response = await ChartOfAccountsService.getAll({ any: ccId });
            this.items = response.content?.data || [];
            this.loading = false;
        },
        async create() {
            try {
                this.chartOfAccounts.costCenterId = this.costCenter.id;
                const response = await ChartOfAccountsService.create(this.chartOfAccounts);
                if (response.success) {
                    this.addEvent({ name: "chartOfAccountsSaved", data: this.chartOfAccounts });
                    this.afterSave();
                }
            } finally {
                this.removeLoading(["btnSaveChartOfAccounts"]);
            }
        },
        async update(param) {
            try {
                const response = await ChartOfAccountsService.update({
                    id: param.id,
                    name: param.name,
                    isPublic: param.isPublic,
                    costCenterId: this.costCenter.id,
                });
                if (response.success) {
                    this.addEvent({ name: "chartOfAccountsSaved", data: this.chartOfAccounts });
                    this.afterSave();
                }
            } catch (e) {
                this.getAll();
            }
        },
        async remove(item) {
            try {
                const response = await ChartOfAccountsService.delete([item.id]);
                if (response.success) this.getAll();
            } finally {
                this.removeLoading(["btnRemove_" + item.id]);
            }
        },
        afterSave() {
            this.getAll();
            const keepCostCenter = this.chartOfAccounts.costCenterId;
            this.resetValidation(this.formName);
            this.chartOfAccounts = new ChartOfAccounts();
            this.chartOfAccounts.costCenterId = keepCostCenter;
        },
    },
    watch: {
        event: {
            deep: true,
            handler(event) {
                if (event.name === "getAllChartOfAccounts") {
                    this.getAll();
                }
            },
        },
    },
};
</script>

<style scoped>
.chart-of-accounts-create-update-list-new {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding-bottom: 30px;
}

.chart-of-accounts-create-update-list-new-input {
    flex: 1;
    min-width: 0;
    position: relative;
}

.chart-of-accounts-create-update-list-new-input>>>.form-group {
    margin-bottom: 0;
}

.chart-of-accounts-create-update-list-new-input>>>.form-group>div:not(.inner-addon) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
}

.chart-of-accounts-create-update-list-new-button {
    flex-shrink: 0;
}

.chart-of-accounts-create-update-list-clean-list {
    list-style: none;
    padding: 0;
    margin: 0;
}

.chart-of-accounts-create-update-list-item-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 12px;
    margin-bottom: 8px;
    background: var(--bg-elevated);
    border: 1px solid var(--border-subtle);
    border-radius: 9px;
}

.chart-of-accounts-create-update-list-item-name {
    flex: 1;
    min-width: 0;
}

.chart-of-accounts-create-update-list-item-actions {
    display: inline-flex;
    gap: .35rem;
    justify-content: flex-end;
    flex-shrink: 0;
}

.chart-of-accounts-create-update-list-empty-state {
    padding: 1.25rem 1rem;
    text-align: center;
    color: var(--text-muted);
    font-size: 13px;
}

.chart-of-accounts-create-update-list-item-icon {
    width: 28px;
    height: 28px;
    border-radius: 50px;
    color: var(--text-inverse);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}
</style>
